import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { InputAdornment, MenuItem, TablePagination, Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { /* StyledWelcomeBanner, */ StyledWrapper } from "../dashboard/style";
import {
  StyledBoxStyle,
  StyledCustomerSearch,
  StyledOrdersContainer,
  StyledStatusFilter,
  StyledTableLayout,
} from "../admin/products/style";
import {
  StyledSearchIcon,
  StyledTypographyText,
} from "../virtual-locker/style";
import { PERSONAL_SHOPPER_ITEMS_LIST } from "../../actions/PersonalShopperActions";
import Loader from "../../components/common/loader/Loader";
import ProductTable from "../../components/common/table/ProductTable";
import ProductImageThumbnails from "../../components/common/table/ProductImageThumbnails";
import ProductOrderIdCell from "../../components/common/table/ProductOrderIdCell";
// import { CompactStatistics } from "../../components/admin/dashboard/StatisticsComponent";
import * as _ from "lodash";
import { productOrderType, productStatusColors, PRODUCT_STATUS_FILTER_OPTIONS, paymentStatusColors, paymentStatusLabels } from "../../utils/common";
import DotIcon from "../../assets/svg/dot-icon.svg";
import { StyledStatusBadge } from "../admin/products/style";
import {
  formatWeightDisplay,
  toPlainWeightNumber,
} from "../../components/modals/order-view-items/orderItemDisplay";

const formatProductStatusLabel = (status) => {
  if (status === "shopping_in_progress") return "In Progress";
  if (status === "shopping_completed") return "Shopping Completed";
  if (status === "received_at_the_warehouse") return "Received at the warehouse";
  return _.startCase(status);
};

const productStatusFilterOptions = PRODUCT_STATUS_FILTER_OPTIONS.map((option) => {
  if (option.value === "shopping_in_progress") return { ...option, label: "In Progress" };
  if (option.value === "shopping_completed") return { ...option, label: "Shopping Completed" };
  return option;
});

const PersonalShopperProducts = () => {
  const dispatch = useDispatch();
  // const [statisticsData, setStatisticsData] = useState(productStatisticsData);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchProducts = useCallback(() => {
    dispatch({
      type: PERSONAL_SHOPPER_ITEMS_LIST,
      payload: {
        page,
        limit: rowsPerPage,
        search: debouncedSearch,
        status: statusFilter,
      },
    });
  }, [dispatch, rowsPerPage, page, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const listPayload = useSelector(
    (state) => state?.persionalShopData?.personalShopperItemsList
  );
  const isLoading = useSelector(
    (state) => state?.persionalShopData?.isPersonalShopperItemsListLoading
  );

  // Reducer stores API `data` payload: { total, page, limit, data: items[], itemCount }
  const rawList = Array.isArray(listPayload?.data) ? listPayload.data : [];
  // const productStatistics = listPayload?.itemCount ?? {};
  const totalRecords = listPayload?.total ?? rawList.length;

  const columns = [
    { key: "rowNo", label: "NO" },
    { key: "order_id", label: "ORDER ID" },
    { key: "order_type", label: "TYPE" },
    { key: "product_name", label: "PRODUCT NAME" },
    { key: "customer_name", label: "CUSTOMER NAME" },
    { key: "quantity", label: "QTY" },
    { key: "weight", label: "WEIGHT" },
    { key: "product_images", label: "IMAGE", minWidth: 120 },
    { key: "status", label: "STATUS" },
    { key: "payment_status", label: "PAYMENT" },
    { key: "actions", label: "ACTIONS" },
  ];

  const allRecords = rawList.map((item, index) => {
    const customer =
      item.create_by_user?.name ||
      item.user?.name ||
      [item.user?.first_name, item.user?.last_name].filter(Boolean).join(" ") ||
      "-";

    return {
      rowNo: (page - 1) * rowsPerPage + index + 1,
      order_id: (
        <ProductOrderIdCell
          orderId={item.order_id}
          orderNumber={item.order_number}
        />
      ),
      order_type: productOrderType[item.order_type] || "Personal Shopper",
      raw_order_type: item.order_type,
      product_name: (
        <Link
          component={RouterLink}
          to={`/personal-shopper/items/${item.id}`}
          underline="hover"
          color="primary"
          sx={{ fontWeight: "normal", cursor: "pointer" }}
        >
          {item.product_name}
        </Link>
      ),
      id: item.id,
      raw_quantity: item.quantity,
      raw_weight: toPlainWeightNumber(item.weight),
      price: item.approx_rate,
      note_to_customer: item.note_to_customer || "",
      customer_name: customer,
      quantity: item.quantity ?? "-",
      product_image: item.product_image || [],
      product_images: (
        <ProductImageThumbnails
          images={item.product_image || []}
          productName={item.product_name}
          productId={item.id}
          recordList={fetchProducts}
          intakeScope="personalShopper"
        />
      ),
      activeStatus: item.status,
      isShopperAssigned: Boolean(item.personal_shopper_id),
      weight: formatWeightDisplay(item.weight),
      payment_status: (
        <StyledStatusBadge background={paymentStatusColors[item.payment_status] || "#9C9C9C"}>
          <img src={DotIcon} alt="" /> {paymentStatusLabels[item.payment_status] || item.payment_status || "Unpaid"}
        </StyledStatusBadge>
      ),
      status: (
        <span
          style={{
            background: productStatusColors[item.status]?.background || "#9c9c9c",
            fontWeight: "bold",
            display: "inline-flex",
            color: "white",
            padding: "2px 8px 2px 6px",
            borderRadius: "20px",
            minWidth: "90px",
            alignItems: "center",
            gap: 5,
          }}
        >
          <img src={DotIcon} alt="" /> {formatProductStatusLabel(item.status)}
        </span>
      ),
    };
  });

  // useEffect(() => {
  //   const updated = productStatisticsData.map((item) => {
  //     switch (item.title) {
  //       case statisticsTitles.verifiedToday:
  //         return { ...item, value: productStatistics?.approved_today || 0 };
  //       case statisticsTitles.pendingApproval:
  //         return { ...item, value: productStatistics?.pending_approvals || 0 };
  //       case statisticsTitles.rejectedItems:
  //         return { ...item, value: productStatistics?.rejected_items || 0 };
  //       case statisticsTitles.itemsAddedToLocker:
  //         return { ...item, value: productStatistics?.added_in_locker || 0 };
  //       default:
  //         return item;
  //     }
  //   });
  //   setStatisticsData(updated);
  // }, [productStatistics]);

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  return (
    <StyledWrapper>
      {/* <StyledWelcomeBanner>
        <CompactStatistics idDataList={statisticsData || []} />
      </StyledWelcomeBanner> */}

      <StyledTableLayout>
        <StyledTypographyText>PRODUCT INTAKE</StyledTypographyText>

        <StyledBoxStyle>
          <StyledCustomerSearch
            placeholder="Search by product name or customer"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <StyledSearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <StyledStatusFilter
            value={statusFilter}
            onChange={handleStatusFilterChange}
            displayEmpty
            inputProps={{ "aria-label": "Filter by status" }}
          >
            {productStatusFilterOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </StyledStatusFilter>
        </StyledBoxStyle>

        <StyledOrdersContainer>
          {isLoading ? (
            <Loader />
          ) : (
            <>
              <ProductTable
                columns={columns}
                rows={allRecords}
                recordList={fetchProducts}
                intakeScope="personalShopper"
              />
              <TablePagination
                component="div"
                count={totalRecords || 0}
                page={page - 1}
                onPageChange={(event, newPage) => setPage(newPage + 1)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(1);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          )}
        </StyledOrdersContainer>
      </StyledTableLayout>
    </StyledWrapper>
  );
};

export default PersonalShopperProducts;
